import { useRouterState } from "@tanstack/react-router";
import { friendlyError } from "@/lib/friendly-errors";
import { AiChatNotice } from "@/components/safety/SafetyNotices";
import { useCallback, useEffect, useRef, useState } from "react";
import { openExternal } from "@/lib/open-external";
import { Link } from "@tanstack/react-router";
import { logQuery } from "@/lib/query-log";
import {
  ArrowRight,
  Leaf,
  LifeBuoy,
  Loader2,
  MessageCircle,
  Minus,
  Phone,
  RotateCcw,
  Send,
  Stethoscope,
  Wind,
  X,
} from "lucide-react";
import { assessCrisis } from "@/lib/crisis-detection";
import { SUPPORT_TOPIC_OPTIONS } from "@/lib/support-topics";
import { submitSupportRequest } from "@/lib/support.functions";
import { supabase } from "@/integrations/supabase/client";
import { ReportProblemDialog } from "@/components/ReportProblemDialog";
import { HOW_TO_USE_SECTIONS, HOW_TO_USE_TITLE } from "@/lib/how-to-use-guide";
import { useAvoidOverlap } from "@/hooks/use-avoid-overlap";

type Msg = { role: "user" | "assistant"; content: string; id: string; guide?: boolean };

const USING_PLATFORM_TOPIC = "Using the Platform";

const STORAGE_KEY = "rt:support-chat";
// How long the floating launcher stays hidden after the user taps the ×.
const HIDE_KEY = "rt:hide-support-chat";
const HIDE_MS = 2 * 60 * 60 * 1000; // a couple of hours
const OPENING =
  "Hi 👋 I'm the Recovery Trauma™ AI support assistant. How can I help?";

const ESCALATE_LINE =
  "I'm not able to resolve this here, but I can send your question to the Recovery Trauma™ team.";

const PRIVACY_NOTE =
  "Please don't include highly sensitive medical or personal information. Messages submitted here may be reviewed by the Recovery Trauma™ support team in order to respond to your enquiry.";

function newId() {
  return Math.random().toString(36).slice(2);
}

function loadStored(): { messages: Msg[]; topic: string | null } {
  if (typeof window === "undefined") return { messages: [], topic: null };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { messages: [], topic: null };
    const parsed = JSON.parse(raw) as { messages?: Msg[]; topic?: string | null };
    return {
      messages: Array.isArray(parsed.messages) ? parsed.messages.slice(-40) : [],
      topic: parsed.topic ?? null,
    };
  } catch {
    return { messages: [], topic: null };
  }
}

/**
 * Recovery Trauma™ support chat.
 * Platform + general support only — never therapy, medical advice or crisis
 * support. Crisis language stops the conversation and surfaces real-world help.
 */
export function SupportChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [topic, setTopic] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [crisis, setCrisis] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [formError, setFormError] = useState<string | null>(null);
  const [formBusy, setFormBusy] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const launcherRef = useRef<HTMLDivElement | null>(null);
  const launcherHidden = useAvoidOverlap(launcherRef, !open && !dismissed, "support-chat");
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  // The floating Support button is homepage-only (Jana, 2026-10-19).
  const onHome = pathname === "/";
  const onWritingPage = /^\/(journal|guided-journal|dream-reflections|sleep-dreams|dissociation-journal|music-journal)/.test(pathname);

  // Restore the conversation (survives navigation and return visits).
  useEffect(() => {
    const stored = loadStored();
    setMessages(stored.messages);
    setTopic(stored.topic);
    setHydrated(true);
    try {
      const until = Number(window.localStorage.getItem(HIDE_KEY) ?? 0);
      setDismissed(Number.isFinite(until) && until > Date.now());
    } catch {
      /* storage unavailable — keep the launcher visible */
    }
  }, []);

  const dismissLauncher = () => {
    try {
      window.localStorage.setItem(HIDE_KEY, String(Date.now() + HIDE_MS));
    } catch {
      /* still hide for this page view */
    }
    setDismissed(true);
  };

  useEffect(() => {
    if (!hydrated) return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ messages: messages.slice(-40), topic }),
      );
    } catch {
      /* storage unavailable — chat still works for this page view */
    }
  }, [messages, topic, hydrated]);

  // Pre-fill name/email for signed-in members.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const u = data.user;
        if (!u || cancelled) return;
        const meta = (u.user_metadata ?? {}) as Record<string, unknown>;
        const name =
          (typeof meta.full_name === "string" && meta.full_name) ||
          (typeof meta.name === "string" && meta.name) ||
          "";
        setForm((f) => ({
          ...f,
          name: f.name || name,
          email: f.email || u.email || "",
        }));
      } catch {
        /* guests simply fill the form themselves */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open, showForm, sent, crisis]);

  useEffect(() => {
    if (open && !showForm && !crisis) inputRef.current?.focus();
  }, [open, showForm, crisis]);

  const send = useCallback(
    async (text: string, chosenTopic?: string) => {
      const trimmed = text.trim();
      if (!trimmed || busy) return;

      // Safety first — crisis language stops the support conversation.
      if (assessCrisis(trimmed).detected) {
        setMessages((m) => [...m, { role: "user", content: trimmed, id: newId() }]);
        setInput("");
        setCrisis(true);
        return;
      }

      // Quietly record the question so common support topics are visible in admin
      logQuery("support", trimmed);

      const nextTopic = chosenTopic ?? topic;
      if (chosenTopic) setTopic(chosenTopic);
      const history = [...messages, { role: "user" as const, content: trimmed, id: newId() }];
      setMessages(history);
      setInput("");
      setBusy(true);
      try {
        const res = await fetch("/api/support-chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: [
              ...(nextTopic && !chosenTopic
                ? []
                : nextTopic
                  ? [{ role: "user" as const, content: `Topic: ${nextTopic}` }]
                  : []),
              ...history.map((m) => ({ role: m.role, content: m.content })),
            ],
          }),
        });
        const json = (await res.json()) as {
          reply?: string;
          escalate?: boolean;
          error?: string;
        };
        if (json.error && !json.reply) {
          setMessages((m) => [
            ...m,
            { role: "assistant", content: `${json.error}`, id: newId() },
          ]);
          if (json.escalate) setShowForm(true);
          return;
        }
        const reply = (json.reply ?? "").trim();
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: json.escalate ? `${reply ? `${reply}\n\n` : ""}${ESCALATE_LINE}` : reply,
            id: newId(),
          },
        ]);
        if (json.escalate) {
          setShowForm(true);
          setForm((f) => ({ ...f, message: f.message || trimmed }));
        }
      } catch {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: `${ESCALATE_LINE}`,
            id: newId(),
          },
        ]);
        setShowForm(true);
        setForm((f) => ({ ...f, message: f.message || trimmed }));
      } finally {
        setBusy(false);
      }
    },
    [busy, messages, topic],
  );

  // "Using the Platform" shows the friendly how-to guide instantly — no wait,
  // no AI round-trip. Follow-up questions still go to the assistant.
  const showGuide = useCallback((t: string) => {
    logQuery("support", t);
    setTopic(t);
    setMessages((m) => [
      ...m,
      { role: "user" as const, content: t, id: newId() },
      { role: "assistant" as const, content: "", id: newId(), guide: true },
    ]);
  }, []);

  const resetChat = () => {
    setMessages([]);
    setTopic(null);
    setCrisis(false);
    setShowForm(false);
    setSent(false);
    setFormError(null);
    setInput("");
  };

  const submitEscalation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formBusy) return;
    setFormError(null);
    if (!form.email.trim() || !form.message.trim()) {
      setFormError("Please add your email and a short message.");
      return;
    }
    setFormBusy(true);
    try {
      const transcript = messages
        .map((m) => `${m.role === "user" ? "Visitor" : "Assistant"}: ${m.content}`)
        .join("\n\n");
      await submitSupportRequest({
        data: {
          name: form.name.trim() || null,
          email: form.email.trim(),
          message: form.message.trim(),
          topic: topic || "Support chat",
          transcript: transcript || null,
        },
      });
      setSent(true);
      setShowForm(false);
    } catch (err) {
      setFormError(
        friendlyError(
          err,
          "We couldn't send that just now. Please try again, or email info@recoverytrauma.com.",
        ),
      );
    } finally {
      setFormBusy(false);
    }
  };

  return (
    <>
      {/* Launcher — shown on the homepage only, so it never sits over
          other pages' content (Jana, 2026-10-19). */}
      {!open && !dismissed && !onWritingPage && onHome && (
        <div
          ref={launcherRef}
          data-floating-control="support-chat"
          className={`fixed end-3 z-[46] transition-all duration-300 sm:end-5 sm:!bottom-[112px] ${launcherHidden ? "pointer-events-none translate-y-2 opacity-0" : "opacity-100"}`}
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)" }}
        >
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open Recovery Trauma support chat"
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[var(--gold-deep)]/60 bg-card/95 px-3 py-2.5 shadow-[0_8px_24px_-10px_rgba(184,134,11,0.55)] backdrop-blur transition-transform hover:scale-105 active:scale-95 sm:px-4"
          >
            <MessageCircle className="h-4 w-4 text-[var(--gold-deep)]" strokeWidth={2.25} />
            <span className="font-display text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground sm:text-xs">
              Support
            </span>
          </button>
          <button
            type="button"
            onClick={dismissLauncher}
            aria-label="Hide the support button for 2 hours"
            title="Hide for 2 hours"
            className="absolute -top-2 -end-2 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition hover:bg-accent hover:text-foreground"
          >
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label="Recovery Trauma support chat"
          className="fixed inset-x-2 bottom-2 z-[70] flex max-h-[80dvh] flex-col overflow-hidden rounded-3xl border border-[var(--gold-deep)]/30 bg-card shadow-2xl sm:inset-x-auto sm:end-5 sm:bottom-6 sm:w-[24rem]"
          style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <header className="flex items-center justify-between gap-2 border-b border-border/60 bg-[var(--gradient-gold)] px-4 py-3">
            <div className="min-w-0">
              <p className="truncate font-display text-sm font-semibold text-foreground">
                Recovery Trauma™ Support
              </p>
              <p className="truncate text-[11px] text-foreground/75">
                Platform &amp; general help — not therapy or crisis support
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={resetChat}
                aria-label="Start a new conversation"
                className="rounded-full p-1.5 text-foreground/70 hover:bg-background/40 hover:text-foreground"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Minimise support chat"
                className="rounded-full p-1.5 text-foreground/70 hover:bg-background/40 hover:text-foreground"
              >
                <Minus className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                }}
                aria-label="Close support chat"
                className="rounded-full p-1.5 text-foreground/70 hover:bg-background/40 hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </header>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            <Bubble role="assistant">{OPENING}</Bubble>

            {messages.length === 0 && !crisis && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {SUPPORT_TOPIC_OPTIONS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() =>
                      t === USING_PLATFORM_TOPIC ? showGuide(t) : void send(t, t)
                    }
                    className="rounded-full border border-[var(--gold-deep)]/35 bg-background px-3 py-1.5 text-xs text-foreground transition hover:bg-[var(--gold-light)]/40"
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}

            {messages.map((m) =>
              m.guide ? (
                <HowToUseGuideCard key={m.id} />
              ) : (
                <Bubble key={m.id} role={m.role}>
                  {m.content}
                </Bubble>
              ),
            )}

            {busy && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Thinking…
              </div>
            )}

            {crisis && <CrisisPanel onContinue={() => setCrisis(false)} />}

            {sent && (
              <div className="rounded-2xl border border-[var(--gold-deep)]/30 bg-[var(--gold-light)]/30 p-3 text-sm text-foreground">
                Thank you. Your message has been sent to the Recovery Trauma™ team.
                We'll reply to you by email.
              </div>
            )}

            {showForm && !crisis && (
              <form
                onSubmit={submitEscalation}
                className="space-y-2 rounded-2xl border border-border/70 bg-background/70 p-3"
              >
                <p className="text-xs font-medium text-foreground">
                  Send this to the Recovery Trauma™ team
                </p>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name (optional)"
                  autoComplete="name"
                  className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm"
                  data-no-translate
                />
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Your email"
                  autoComplete="email"
                  className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm"
                  data-no-translate
                />
                <textarea
                  required
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="How can we help?"
                  className="w-full resize-none rounded-xl border border-border bg-card px-3 py-2 text-sm"
                  data-no-translate
                />
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  {PRIVACY_NOTE}
                </p>
                {formError && (
                  <p className="text-[11px] text-destructive">{formError}</p>
                )}
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="rounded-full px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                  >
                    Not now
                  </button>
                  <button
                    type="submit"
                    disabled={formBusy}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gradient-gold)] px-4 py-2 text-xs font-semibold text-foreground disabled:opacity-60"
                  >
                    {formBusy ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ArrowRight className="h-3.5 w-3.5" />
                    )}
                    Send to the team
                  </button>
                </div>
              </form>
            )}
          </div>

          <AiChatNotice className="border-t border-border px-3 pt-2" />

          {!crisis && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void send(input);
              }}
              className="flex items-end gap-2 border-t border-border/60 bg-card px-3 py-2.5"
            >
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void send(input);
                  }
                }}
                placeholder="Type your question…"
                aria-label="Type your question"
                data-no-translate
                className="max-h-24 min-h-10 flex-1 resize-none rounded-2xl border border-border bg-background px-3 py-2 text-sm"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                aria-label="Send message"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-foreground disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          )}

          {!crisis && (
            <p className="border-t border-border/50 bg-background/60 px-4 py-2 text-[10px] leading-relaxed text-muted-foreground">
              This chat is for platform and general support — not therapy, medical
              advice or emergency help.{" "}
              <Link to="/crisis" className="underline underline-offset-2">
                Crisis support
              </Link>
              {" · "}
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                className="underline underline-offset-2"
              >
                Report a problem
              </button>
            </p>
          )}
          <ReportProblemDialog open={reportOpen} onOpenChange={setReportOpen} />
        </div>
      )}
    </>
  );
}

function Bubble({
  role,
  children,
}: {
  role: "user" | "assistant";
  children: React.ReactNode;
}) {
  if (role === "user") {
    return (
      <div className="flex justify-end">
        <div
          data-user-content
          data-no-translate
          className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-[var(--gold-light)]/60 px-3 py-2 text-sm text-foreground"
        >
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-[92%] whitespace-pre-wrap text-sm leading-relaxed text-foreground">
      {typeof children === "string" ? renderLightMarkdown(children) : children}
    </div>
  );
}

/** Friendly "How to Use Recovery Trauma™" guide card for the Using the Platform topic. */
function HowToUseGuideCard() {
  return (
    <div className="max-w-[96%] rounded-2xl border border-[var(--gold-deep)]/30 bg-[var(--gold-light)]/20 p-3.5">
      <p className="font-display text-sm font-semibold text-foreground">
        {HOW_TO_USE_TITLE}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        A quick tour — tap any section to read it.
      </p>
      <div className="mt-2.5 space-y-1.5">
        {HOW_TO_USE_SECTIONS.map((s) => (
          <details
            key={s.title}
            className="group rounded-xl border border-border/60 bg-card/80 px-3 py-2"
          >
            <summary className="flex cursor-pointer list-none items-center gap-2 text-[13px] font-medium text-foreground [&::-webkit-details-marker]:hidden">
              <span aria-hidden className="text-base leading-none">
                {s.icon}
              </span>
              <span className="min-w-0 flex-1">{s.title}</span>
              <ArrowRight
                aria-hidden
                className="h-3.5 w-3.5 shrink-0 text-[var(--gold-deep)] transition-transform group-open:rotate-90"
              />
            </summary>
            <p className="mt-1.5 pl-7 text-xs leading-relaxed text-foreground/85">
              {s.body}
            </p>
          </details>
        ))}
      </div>
      <p className="mt-2.5 text-xs leading-relaxed text-muted-foreground">
        Anything else you'd like to know? Just type your question below. 💛
      </p>
    </div>
  );
}

/** Minimal **bold** / *italic* rendering so model formatting doesn't leak. */
function renderLightMarkdown(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return (
        <strong key={i} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{part.replace(/(^|\s)\*(\S[^*]*)\*/g, "$1$2")}</span>;
  });
}

/** Safety takeover — shown instead of continuing the support conversation. */
function CrisisPanel({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="rounded-2xl border border-[var(--gold-deep)]/40 bg-[var(--gold-light)]/30 p-4">
      <p className="font-display text-sm font-semibold text-foreground">
        🤍 Your safety matters.
      </p>
      <p className="mt-2 text-sm leading-relaxed text-foreground/90">
        <span className="font-semibold">Recovery Trauma™ is not a crisis service</span>{" "}
        and this chat can't offer emergency help. If you are in immediate danger,
        having thoughts of self-harm, or need urgent support, please contact local
        emergency services or a crisis line.
      </p>
      <ul className="mt-3 space-y-1.5 text-sm text-foreground/90">
        <li className="flex items-start gap-2">
          <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--gold-deep)]" />
          <span>
            <span className="font-medium">US &amp; Canada:</span> call or text{" "}
            <span className="font-semibold">988</span>
          </span>
        </li>
        <li className="flex items-start gap-2">
          <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--gold-deep)]" />
          <span>
            <span className="font-medium">UK &amp; ROI:</span> call{" "}
            <span className="font-semibold">116 123</span> (Samaritans)
          </span>
        </li>
        <li className="flex items-start gap-2">
          <LifeBuoy className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--gold-deep)]" />
          <span>
            <span className="font-medium">Worldwide:</span>{" "}
            <a
              href="https://findahelpline.com"
              onClick={(e) => openExternal("https://findahelpline.com", e)}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2"
            >
              findahelpline.com
            </a>{" "}
            · Emergency: 999 (UK), 911 (US), 112 (EU)
          </span>
        </li>
      </ul>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <CalmLink to="/grounding" icon={Leaf} label="Grounding" />
        <CalmLink to="/breathing" icon={Wind} label="Breathing" />
        <CalmLink to="/therapists" icon={Stethoscope} label="Find a therapist" />
        <CalmLink to="/crisis" icon={LifeBuoy} label="Crisis support" />
      </div>
      <button
        type="button"
        onClick={onContinue}
        className="mt-3 w-full rounded-full border border-border bg-card px-3 py-2 text-xs text-foreground"
      >
        Continue with a support question
      </button>
    </div>
  );
}

function CalmLink({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: typeof Leaf;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-3 py-2 text-xs font-medium text-foreground"
    >
      <Icon className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
      {label}
    </Link>
  );
}
